import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
  setColors,
  setProjectInput,
} from '../../element-editor/store/editor.actions';
import {
  selectColors,
  selectHasColors,
} from '../../element-editor/store/editor.selectors';
import { ColorEditorOverlayService } from '../../shared/overlays/color-editor-overlay/color-editor-overlay.service';
import { HttpService, RequestType } from '../../store/http/http.service';
import { ShapeService } from '../../element-editor/shape/shape.service';
import { Router } from '@angular/router';
import { IdToIRI, IRIToId } from '../../util/iri';
import { selectNewProjectName } from '../../element-editor/store/selector/editor.selector';
import {
  currentOrganisationID,
  selectCurrentOrganizationID,
} from '../../organisation/organisation.selector';
import { omit } from 'lodash';
import { FileService, ProcessedFile } from '../../services/file/file.service';
import { ResourceData } from '../../elements/resource/resource.types';
import { ErrorService } from '../../services/error/error.service';
import {
  selectProjectsByEditTime,
  StudioProject,
} from '../../element-editor/store/studio';

export type MediaKind = 'image' | 'video' | 'doc';
export type DocSubType = 'pdf' | 'ppt';
export type ProjectCreationMode = 'scratch' | 'prompt' | null;

export interface Prompt {
  text?: string;
  media: ProcessedFile[];
}

@Component({
  selector: 'app-new-project-component',
  templateUrl: './new-project-component.component.html',
  styleUrls: ['./new-project-component.component.scss'],
})
export class NewProjectComponentComponent implements OnInit, OnDestroy {
  @ViewChild('overlayCloseBtn') overlayCloseBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('imageInput') imageInput?: ElementRef<HTMLInputElement>;
  @ViewChild('videoInput') videoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('docInput') docInput?: ElementRef<HTMLInputElement>;
  @Input() questionnaire: ResourceData;
  @Input() customToken: string;

  @Output() finished = new EventEmitter();

  get questionnaireMode() {
    return !!this.questionnaire;
  }

  /** Project creation mode: 'scratch' for empty project, 'prompt' for AI-assisted */
  creationMode: ProjectCreationMode = null;

  file?: File;
  filePreviewUrl?: string;
  fileType?: 'image' | 'video';

  imageUploadState: 'initial' | 'dragging' | 'dropped' = 'initial';

  // Colors selectors
  colors$: Observable<string[]> = this.store.select(selectColors);
  hasColors$: Observable<boolean> = this.store.select(selectHasColors);

  errorMsg = '';

  /** Maximum number of files that can be uploaded */
  readonly maxFiles = 10;

  /** File size limits in bytes */
  readonly maxFileSizes = {
    image: 10 * 1024 * 1024, // 10 MB
    video: 1 * 1024 * 1024 * 1024, // 1 GB
    pdf: 25 * 1024 * 1024, // 25 MB
    docx: 15 * 1024 * 1024, // 15 MB
  };

  pendingPrompt: Prompt = { text: '', media: [] };
  addedPrompts: Prompt[] = [];

  // Media model for overlay + thumbnails (see OverlayItem interface above)

  // Overlay preview state
  overlayOpen = false;
  overlayIndex = 0;
  overlayItems: ProcessedFile[] = [];
  lastTriggerEl: HTMLElement | null = null;

  submissionLoading = false;
  submissionSuccess = false;

  state: 'mode-selection' | 'input' | 'loading' | 'success' = 'mode-selection';
  newProjectName = '';

  projects$: Observable<StudioProject[]>;
  projects: StudioProject[] = [];
  get textAreaPlaceHolder() {
    // return this.pendingPrompt.media.length
    //   ? 'Describe the image'
    //  : 'Tell us about your video use-case and drag & drop images, videos, and files';
    return 'Please describe your video use-case and drag & drop images, videos, and files';
  }

  selectCreationMode(mode: ProjectCreationMode) {
    this.creationMode = mode;
    if (mode === 'scratch') {
      this.submitScratchProject();
    }
  }

  private async submitScratchProject() {
    this.submissionLoading = true;

    const textId = this.shapeService.getNewID();
    const projectSavePromise = this.http
      .requestCall({
        type: RequestType.POST,
        path: 'project/scratch',
        body: {
          label: 'New Project',
          orgIRI: IdToIRI(this.currentOrgId),
          scratch: true,
          sceneDescriptor: {
            type: 'scene',
            texts: [textId],
            shapes: {
              [textId]: {
                text: 'Hello world',
                cell: {
                  x: 0.2,
                  y: 0,
                  w: 0.6,
                  h: 1,
                },
              },
            },
          },
        },
      })
      .pipe(
        map(resp => {
          const { project, files } = resp;
          console.log('response - scratch project', project, 'files', files);
          this.store.dispatch(setProjectInput({ project, files }));
          this.submissionLoading = false;
          this.router.navigate(['p', IRIToId(project.IRI)]);
          return resp;
        }),
      )
      .toPromise();

    await projectSavePromise;
  }

  async submitNewProject() {
    // console.log('submitNewProject', this.pendingPrompt); //
    const { text, media } = this.pendingPrompt;

    if (!text) {
      this.errorMsg = 'Please add some text';
      setTimeout(() => {
        this.errorMsg = '';
      }, 3000);
      return;
    }

    // const mediaFiles = media.map(({ file, id }) => ({ file, id }));

    this.submissionLoading = true;
    if (text || media.length) {
      this.addedPrompts.push({
        text: text?.trim() || undefined,
        media,
      });
    }

    // if (this.customToken) {
    //   const questionnairSave = this.http
    //     ._patchCustomProject(
    //       {
    //         questionnaireIRI: this.questionnaire.IRI,
    //         useCase: this.addedPrompts,
    //       },
    //       this.customToken,
    //     )
    //     .toPromise();

    //   await Promise.all([...uploadPromises, questionnairSave]);
    //   this.finished.emit();
    //   return;
    // }

    const fileUploadPromise = this.fileService.uploadFles(media);

    const projectSavePromise = this.http
      .requestCall({
        type: RequestType.POST,
        path: 'project/chat',
        body: {
          name: 'Chatbot Project',
          orgIRI: IdToIRI(this.currentOrgId),
          text,
          files: media.map(f => f.meta),
        },
      })
      .pipe(
        map(resp => {
          console.log('projext');
          // const { project, files } = resp;
          // console.log('response - project', project, 'files', files);
          // this.store.dispatch(setProjectInput({ project, files }));
          // this.submissionLoading = false;
          // this.router.navigate(['p', IRIToId(project.IRI)]);
          return resp;
        }),
        catchError(error => {
          // console.error('error', error);
          // return of(error);
          this.errorService.handleError(error.message, {
            context: 'submitNewProject-by-chat',
          });
          return of(error);
        }),
      )
      .toPromise();

    this.state = 'loading';
    await Promise.all([fileUploadPromise, projectSavePromise]);
    // -- // console.log('addPrompts', this.addedPrompts); // -- //
    this.state = 'success';
    this.clear();
    setTimeout(() => {
      this.state = 'input';
    }, 3000);
  }

  clear() {
    this.pendingPrompt = { text: '', media: [] };
    this.addedPrompts = [];
    this.previewUrls = [];
    this.imageUploadState = 'initial';
  }

  keyup(event: KeyboardEvent) {
    // -- // -- //
    if (event.key === 'Enter') {
      this.submitPrompt();
    }
  }

  hover(hover: boolean) {
    console.log('hover-event', hover);
    if (!hover) {
      this.imageUploadState = 'initial';
      return;
    }
    this.imageUploadState = 'dragging';
  }

  previewUrls: string[] = [];
  pendingFiles: File[] = [];

  async onFileDropped(event: { files?: FileList | File[] } | null) {
    this.imageUploadState = 'dropped';
    // -- // -- // -- //
    const raw = event?.files;
    const files: File[] = raw
      ? raw instanceof FileList
        ? Array.from(raw)
        : (raw as File[])
      : [];
    if (!files.length) return;

    for (const file of files) {
      if (this.pendingPrompt.media.length >= this.maxFiles) {
        this.showError(`Maximum ${this.maxFiles} files allowed.`);
        break;
      }
      await this.processFile(file);
    }

    this.rebuildOverlayItems();
  }

  triggerFileInput(kind: 'image' | 'video' | 'doc') {
    console.log(
      'triggerFileInput',
      kind,
      'docInput',
      this.docInput?.nativeElement,
    );
    const el =
      kind === 'image'
        ? this.imageInput?.nativeElement
        : kind === 'video'
          ? this.videoInput?.nativeElement
          : this.docInput?.nativeElement;
    el?.click();
  }

  async onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const list = input.files;
    if (!list || list.length === 0) {
      input.value = '';
      return;
    }

    // mirror drag-and-drop flow // -- // -- //
    this.imageUploadState = 'dropped';

    const files: File[] = Array.from(list);
    for (const file of files) {
      if (this.pendingPrompt.media.length >= this.maxFiles) {
        this.showError(`Maximum ${this.maxFiles} files allowed.`);
        break;
      }
      await this.processFile(file);
    }

    this.rebuildOverlayItems();

    // allow selecting the same files again later
    input.value = '';
  }

  showError(msg: string) {
    this.errorMsg = msg;
    setTimeout(() => (this.errorMsg = ''), 4000);
  }

  currentOrgId$ = this.store.select(selectCurrentOrganizationID);
  currentOrgId: string;
  constructor(
    private store: Store<unknown>,
    private colorOverlay: ColorEditorOverlayService,

    private http: HttpService,

    private shapeService: ShapeService,
    public router: Router,
    private fileService: FileService,
    private errorService: ErrorService,
  ) {
    // console.log('new-project-component > constructor', this.fileService); //
    this.store.select(currentOrganisationID).subscribe(id => {
      this.currentOrgId = id;
      // console.log('currentOrgId', id);
    });

    this.projects$ = this.store.select(selectProjectsByEditTime);

    this.projects$.subscribe(projects => {
      this.projects = projects;
      console.log('projects', projects); // -- //
    });

    this.store.select(selectNewProjectName).subscribe(name => {
      this.newProjectName = name;
    });
    this.currentOrgId$.subscribe(id => {
      this.currentOrgId = id;
    });
  }

  submitPrompt() {
    const text = (this.pendingPrompt.text || '').trim();
    const files = this.pendingPrompt.media;

    if (!text && files.length === 0) return;

    this.addedPrompts.push({ text: text || undefined, media: [...files] });

    // reset pending
    this.pendingPrompt = { text: '', media: [] };
    this.previewUrls = [];
    this.imageUploadState = 'initial';

    this.rebuildOverlayItems();
  }

  removePendingFile(index: number, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const f = this.pendingPrompt.media[index];
    if (!f) return;
    // Revoke local object URLs for non-image previews if needed
    if (
      (f.meta.type === 'video' || f.meta.type === 'doc') &&
      f.preview.objectUrl?.startsWith('blob:')
    ) {
      try {
        URL.revokeObjectURL(f.preview.objectUrl);
      } catch {
        /* noop */
      }
    }
    this.pendingPrompt.media.splice(index, 1);
    this.rebuildOverlayItems();

    if (this.overlayOpen) {
      if (this.overlayIndex >= this.overlayItems.length) {
        this.overlayIndex = Math.max(0, this.overlayItems.length - 1);
      }
      if (this.overlayItems.length === 0) {
        this.closeOverlay();
      }
    }
  }

  // Overlay lifecycle
  ngOnInit(): void {
    this.rebuildOverlayItems();
  }
  ngOnDestroy(): void {
    const allFiles: ProcessedFile[] = [
      ...this.pendingPrompt.media,
      ...this.addedPrompts.flatMap(p => p.media || []),
    ];
    allFiles.forEach(f => {
      if (
        (f.meta.type === 'video' || f.meta.type === 'doc') &&
        f.preview.objectUrl?.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(f.preview.objectUrl);
        } catch (e) {
          /* noop */
        }
      }
    });
  }

  private rebuildOverlayItems() {
    const promptMedia: any[] = [];
    for (const p of this.addedPrompts) {
      (p.media || []).forEach(f => promptMedia.push({ ...f }));
    }
    // pending first
    this.overlayItems = [...this.pendingPrompt.media, ...promptMedia];
  }

  openOverlay(index: number, trigger?: EventTarget | null) {
    return;
    if (!this.overlayItems.length) return;
    this.overlayIndex = Math.max(
      0,
      Math.min(index, this.overlayItems.length - 1),
    );
    this.overlayOpen = true;
    this.lastTriggerEl = (trigger as HTMLElement) || null;
    // focus close button shortly after render
    setTimeout(() => this.overlayCloseBtn?.nativeElement?.focus(), 0);
    this.preloadNeighbors();
  }

  closeOverlay() {
    this.pauseOverlayVideo();
    this.overlayOpen = false;
    const el = this.lastTriggerEl;
    this.lastTriggerEl = null;
    if (el) {
      el.focus();
    }
  }

  private pauseOverlayVideo() {
    const v = document.querySelector(
      '.overlay-content video',
    ) as HTMLVideoElement | null;
    if (v) {
      try {
        v.pause();
      } catch (e) {
        /* noop */
      }
    }
  }

  next() {
    if (this.overlayIndex < this.overlayItems.length - 1) {
      this.pauseOverlayVideo();
      this.overlayIndex++;
      this.preloadNeighbors();
    }
  }

  /** Extracts a "uuid" from an IRI (last segment after / or #). */
  private iriSuffix(iri: string): string {
    const m = iri.match(/[^/#]+$/);
    return m ? m[0] : iri;
  }

  /** Converts a data URL to base64 payload (no mime header). */
  private dataUrlToBase64(dataUrl: string): string {
    const commaIdx = dataUrl.indexOf(',');
    return commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
  }

  /** Fetches a blob and returns base64 string. Works for blob: or http(s) URLs. */
  private async urlToBase64(url: string): Promise<string> {
    const res = await fetch(url);
    const blob = await res.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++)
      binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  /** Best-effort file extension from name or mime. */
  private guessExtension(name?: string, mime?: string): string {
    if (name && /\.[a-z0-9]+$/i.test(name))
      return name.split('.').pop()!.toLowerCase();
    if (!mime) return 'bin';
    if (mime.startsWith('image/')) return mime.split('/')[1];
    if (mime.startsWith('video/')) return mime.split('/')[1];
    if (mime === 'application/pdf') return 'pdf';
    if (mime.includes('presentation')) return 'pptx';
    if (mime.includes('powerpoint')) return 'ppt';
    return 'bin';
  }

  prev() {
    if (this.overlayIndex > 0) {
      this.pauseOverlayVideo();
      this.overlayIndex--;
      this.preloadNeighbors();
    }
  }

  onOverlayKeydown(e: KeyboardEvent) {
    if (!this.overlayOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeOverlay();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    }
  }

  onBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      this.closeOverlay();
    }
  }

  get currentItem() {
    return this.overlayItems[this.overlayIndex];
  }

  private overlayIndexForProcessedFile(
    promptIndex: number,
    fileIndex: number,
  ): number | null {
    let idx = this.pendingPrompt.media.length; // pending first
    for (let i = 0; i < this.addedPrompts.length; i++) {
      const files = this.addedPrompts[i]?.media || [];
      if (files.length) {
        if (i === promptIndex) return idx + fileIndex;
        idx += files.length;
      }
    }
    return null;
  }

  onProcessedFileClick(
    promptIndex: number,
    fileIndex: number,
    target: EventTarget,
  ) {
    const oi = this.overlayIndexForProcessedFile(promptIndex, fileIndex);
    if (oi !== null) this.openOverlay(oi, target);
  }

  private preloadNeighbors() {
    const prev = this.overlayItems[this.overlayIndex - 1];
    const next = this.overlayItems[this.overlayIndex + 1];
    [prev, next].forEach(item => {
      if (!item) return;
      if (item.meta.type === 'image') {
        const img = new Image();
        img.src = item.preview.objectUrl;
      }
      // do not pre-buffer videos, just let metadata load when selected
    });
  }

  private async processFile(file: File) {
    const result = await this.fileService.processFile(file);
    this.pendingPrompt.media.push(result);
  }

  async openColorEditor() {
    const initial = await new Promise<string[]>(resolve => {
      const sub = this.colors$.pipe(take(1)).subscribe(colors => {
        resolve(colors || []);
        sub?.unsubscribe();
      });
    });
    const result = await this.colorOverlay.open(initial);
    if (result) {
      this.store.dispatch(setColors({ colors: result }));
    }
  }
}
